import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Button,
    Flex,
    HStack,
    Text,
    VStack
} from "@chakra-ui/react";
import {DealListForm} from "../../form/DealListForm";
import {BarDeal, ChainDeal, NewBarDealSuggestion, NewChainDealSuggestion} from "../../../interfaces/IDeal";
import {BarChain, NewBarSubmission} from "../../../interfaces/Bar";
import {useTranslations} from "next-intl";
import {useState} from "react";
import {CloseIcon, EditIcon, EmailIcon} from "@chakra-ui/icons";
import {EmailLink} from "../widget/EmailLink";
import {HideableField} from "../../form/HideableField";

export interface EditorProps {
    bar: NewBarSubmission,
    initialDeals: BarDeal[],
    deals: NewBarDealSuggestion[],
    dealsChanged: (deals: NewBarDealSuggestion[]) => void,
    chain: BarChain|undefined,
    initialChainDeals: ChainDeal[],
    chainDeals: NewChainDealSuggestion[],
    chainDealsChanged: (deals: NewChainDealSuggestion[]) => void,
    markClosed: () => void,
    chainDescriptionChanged: (description: string) => void,
    barDescriptionChanged: (description: string) => void,
    onActivated: (active: boolean) => void
}

export default function Editor(
    {bar, initialDeals, deals, dealsChanged, chain, initialChainDeals, chainDeals, chainDealsChanged, markClosed, onActivated, chainDescriptionChanged, barDescriptionChanged}: EditorProps
) {
    const t = useTranslations("addbar")
    const [expanded, setExpanded] = useState<number[]|number>([])
    return (
        <VStack alignItems="left" w="100%" gap={10}>
            <Accordion allowToggle index={expanded} onChange={setExpanded}>
                <AccordionItem borderRadius="10px" marginTop="5px" borderWidth="2px">
                    <AccordionButton>
                        <HStack justify="space-between" w="100%">
                            <CloseIcon/>
                            <Text>{t("is_closed")}</Text>
                            <AccordionIcon/>
                        </HStack>
                    </AccordionButton>
                    <AccordionPanel>
                        <Button variant="error" onClick={() => {
                            setExpanded(-1)
                            setTimeout(markClosed, 700)
                        }}>
                            {t("mark_closed")}
                        </Button>
                    </AccordionPanel>
                </AccordionItem>
                <AccordionItem borderRadius="10px" marginTop="10px" borderWidth="2px">
                    <AccordionButton>
                        <HStack justify="space-between" w="100%">
                            <EmailIcon/>
                            <Text>{t("cant_use_form")}</Text>
                            <AccordionIcon/>
                        </HStack>
                    </AccordionButton>
                    <AccordionPanel>
                        <Text>{t.raw("cant_use_form_text")}</Text>
                        <EmailLink email="info@happyhours.lv" subject={t("email_subject", {bar: bar.name ?? "<New bar>"})} text={encodeURIComponent(t.raw("email_text"))}/>
                    </AccordionPanel>
                </AccordionItem>
                { chain &&
                    <AccordionItem borderRadius="10px" marginTop="10px" borderWidth="2px">
                        <AccordionButton>
                            <HStack justify="space-between" w="100%">
                                <EditIcon/>
                                <Text>{t("chain_description", {name: chain.name})}</Text>
                                <AccordionIcon/>
                            </HStack>
                        </AccordionButton>
                        <AccordionPanel>
                            <HideableField label={t("chain_description", {name: chain.name})} value={chain.description ?? ""} onChange={chainDescriptionChanged} isShown={true} onChangeVisibility={value => {setExpanded(-1)}}/>
                        </AccordionPanel>
                    </AccordionItem>
                }
                <AccordionItem borderRadius="10px" marginTop="10px" borderWidth="2px">
                    <AccordionButton>
                        <HStack justify="space-between" w="100%">
                            <EditIcon/>
                            <Text>{t.rich("chain_description", {name: bar.name})}</Text>
                            <AccordionIcon/>
                        </HStack>
                    </AccordionButton>
                    <AccordionPanel>
                        <HideableField label={t("chain_description", {name: bar.name})} value={bar.description ?? ""} onChange={barDescriptionChanged} isShown={true} onChangeVisibility={value => {setExpanded(-1)}}/>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
            <Flex direction={initialDeals.length > 0 ? "column" : "column-reverse"} gap={4}>
                <DealListForm<BarDeal, NewBarDealSuggestion> initialDeals={initialDeals} deals={deals} onChange={dealsChanged} onActivated={onActivated} title={t("deals")}/>
                {chain && (
                    <>
                        <DealListForm<ChainDeal, NewChainDealSuggestion> initialDeals={initialChainDeals}
                                                                         deals={chainDeals}
                                                                         onChange={chainDealsChanged}
                                                                         title={t("deals_chain", {chain: chain.name})}
                                                                         onActivated={onActivated}/>
                    </>
                )}
            </Flex>
        </VStack>
    )
}